/**
 * Attendance history: any date range, any class, with an export.
 *
 * The pickup roster answers "where is everyone right now" and deliberately shows one week.
 * This answers the questions that arrive later — who collected a child on the 14th, which
 * handovers went to someone not on the authorized list, how often a family was absent — and
 * it has to reach back over a whole term to do it.
 *
 * **Filtered and paged in the database, not in the browser.** Today the table holds under
 * two thousand rows and everything would fit in memory, but a full school year is roughly a
 * hundred students times a hundred and eighty days times three class rows each — far past
 * what is reasonable to ship to a phone, and past PostgREST's default ceiling besides. Every
 * filter below is a query predicate, so the page stays the same size in year three.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { AttendanceHistoryRow } from '../utils/attendanceHistory';
import { REAL_SESSION_KEY, type AttendanceStatus } from '../types/attendance';
import { PILLAR_LABEL, PILLAR_ORDER, type PillarId } from '../staff-app/staffPillars';
import {
  buildAttendanceFacets,
  type AttendanceFacets,
  type FacetSelection,
  type FacetStudent,
} from '../utils/attendanceFacets';

/** Rows per page in the table. */
export const HISTORY_PAGE_SIZE = 100;

/** Rows per round trip when assembling an export. PostgREST's own ceiling is 1000. */
const EXPORT_CHUNK = 1000;

/**
 * Hard ceiling on an export.
 *
 * Not a performance guard so much as an honesty one: a spreadsheet nobody can open is worse
 * than a refusal, and an admin who genuinely wants three years of history is better served
 * by narrowing the range than by a browser tab that dies building the string. The page says
 * plainly when a range hits this rather than silently handing over a partial file.
 */
export const EXPORT_ROW_CAP = 20_000;

/**
 * Which band of classes to show, or all of them.
 *
 * The filter is deliberately coarse. Naming an individual program or one of the fourteen
 * virtual classes meant a picker of a hundred-odd entries, and the finer distinction is
 * already on every row — the Class column names the program or the class the record belongs
 * to. So the dropdown asks the question a person actually starts from and the page carries
 * the breakdown.
 *
 * The teacher portal's four pillars **plus Camps**, which is why this is its own type rather
 * than `PillarId`. A teacher never sees a Camps entry: their menu is built from the rosters
 * running today, so in July the camp simply *is* their After School equivalent. An archive is
 * the other case — it spans the whole year at once, and camps are the bulk of it (1,151 of
 * 1,759 records). Folding them into Enrichment, as `pillarOfProgramType` does, left one
 * bucket holding two thirds of everything and another holding 29 rows.
 *
 * The five partition the table exactly: on prod, 379 + 29 + 1,151 + 100 + 100 = 1,759, the
 * whole of `attendance_records`. No row is in two bands and none is in none.
 *
 * **There is no "all bands".** A table mixing a Chinese register with a camp handover is not
 * a comparison anyone can make — the rows count different kinds of event — and an export of
 * it would be a spreadsheet whose totals mean nothing. One band is always selected, so every
 * query and every file is about one kind of thing.
 */
export type HistoryBand = PillarId | 'camp';

/**
 * The five bands in the order the year runs: term-time first, then the summer, then the two
 * classes that sit inside after-school.
 *
 * Shared by every surface that offers the choice — the admin archive and the teacher
 * portal's — so the two cannot end up naming or ordering them differently.
 */
export const HISTORY_BAND_ORDER: readonly HistoryBand[] = [
  PILLAR_ORDER[0],
  PILLAR_ORDER[1],
  'camp',
  PILLAR_ORDER[2],
  PILLAR_ORDER[3],
];

/**
 * Labels for the four teacher-portal pillars come from `PILLAR_LABEL`, so a rename cannot
 * leave an admin's filter and a teacher's menu disagreeing. Camps has no pillar to borrow
 * from — a teacher's roster menu never shows one, because in July the camp simply *is*
 * their roster — so it is named here.
 */
export const HISTORY_BAND_LABEL: Record<HistoryBand, string> = {
  ...PILLAR_LABEL,
  camp: 'Camps',
};

/** `programs.type` values that get their own band; everything else is Enrichment. */
const NAMED_PROGRAM_TYPES = ['after-school', 'camp'] as const;

export interface AttendanceHistoryFilters {
  /** Inclusive YYYY-MM-DD bounds, business TZ. */
  from: string;
  to: string;
  band: HistoryBand;
  status: AttendanceStatus | 'all';
  /** Only handovers recorded against someone not on the authorized list. */
  unauthorizedOnly: boolean;
  /** Matches the student's first or last name. */
  studentSearch: string;
  /**
   * Matches whoever collected the child, or whoever recorded the check-in.
   *
   * Separate from the student box rather than one combined search: PostgREST scopes an `or`
   * to a single relation, and the student's name lives on the joined table while the
   * collector's lives on this one. Two boxes is the honest shape of that — and they compose,
   * so "did Sara ever collect Ada" is one query rather than a scan of the results.
   */
  collectorSearch: string;
  /**
   * Which grade, or which band of grades. One control rather than two: a band is just the
   * union of the grades under it, so "TK & K" and "G3" are the same kind of question asked at
   * two zoom levels, and two separate dropdowns would let them contradict each other.
   */
  grade: FacetSelection;
  school: FacetSelection;
  /**
   * One of the fourteen virtual classes, e.g. `'chinese:3'`, or null for the whole subject.
   *
   * Chinese and Math narrow by grade this way rather than through `grade` above: the class
   * *is* the grade, so the column already holds the answer and there is no jsonb lookup or
   * spelling problem to solve.
   */
  virtualClassKey: string | null;
}

export interface AttendanceHistoryPage {
  rows: AttendanceHistoryRow[];
  /** Total matching the filters, not the number returned on this page. */
  total: number;
}

interface HistoryRowShape {
  id: string;
  student_id: string;
  session_date: string;
  status: AttendanceStatus;
  virtual_class_key: string | null;
  checked_in_at: string | null;
  checked_in_by: string | null;
  checked_in_by_name: string | null;
  picked_up_at: string | null;
  picked_up_by_name: string | null;
  pickup_is_authorized: boolean | null;
  pickup_recorded_by_name: string | null;
  absence_reason: string | null;
  student: {
    first_name: string;
    last_name: string;
    metadata: { grade?: string; school?: string } | null;
  } | null;
  program: { name: string; type: string } | null;
}

const SELECT =
  'id, student_id, session_date, status, virtual_class_key, checked_in_at, checked_in_by, ' +
  'checked_in_by_name, picked_up_at, picked_up_by_name, pickup_is_authorized, ' +
  'pickup_recorded_by_name, absence_reason, ' +
  // Both embeds are `!inner` so they are filterable joins rather than optional lookups — the
  // pillar filter constrains `program.type`, which an outer embed could not express.
  'student:students!inner(first_name, last_name, metadata), program:programs!inner(name, type)';

function toHistoryRow(row: HistoryRowShape): AttendanceHistoryRow {
  const first = row.student?.first_name ?? '';
  const last = row.student?.last_name ?? '';
  return {
    id: row.id,
    studentId: row.student_id,
    studentName: [first, last].filter(Boolean).join(' ') || 'Unknown student',
    studentSortName: last && first ? `${last}, ${first}` : last || first || 'Unknown student',
    studentGrade: row.student?.metadata?.grade ?? null,
    studentSchool: row.student?.metadata?.school ?? null,
    sessionDate: row.session_date,
    status: row.status,
    virtualClassKey: row.virtual_class_key ?? REAL_SESSION_KEY,
    programName: row.program?.name ?? null,
    programType: row.program?.type ?? null,
    checkedInAt: row.checked_in_at,
    checkedInBy: row.checked_in_by,
    checkedInByName: row.checked_in_by_name,
    pickedUpAt: row.picked_up_at,
    pickedUpByName: row.picked_up_by_name,
    pickupIsAuthorized: row.pickup_is_authorized,
    pickupRecordedByName: row.pickup_recorded_by_name,
    absenceReason: row.absence_reason,
  };
}

/**
 * PostgREST splits an `or` on commas and reads parentheses as grouping, so either character
 * arriving from a search box would corrupt the filter rather than match anything.
 */
function escapeSearchTerm(term: string): string {
  return term.replace(/[,()]/g, ' ').trim();
}

/**
 * One grade/school predicate.
 *
 * `missing` becomes `IS NULL` rather than an empty `IN ()`, which would match nothing and
 * read as "no data" instead of "no value on file" — the opposite of the question being asked.
 */
function applyFacet<T extends { in: (c: string, v: string[]) => T; is: (c: string, v: null) => T }>(
  query: T,
  column: string,
  selection: FacetSelection,
): T {
  if (selection.kind === 'missing') return query.is(column, null);
  if (selection.kind === 'values' && selection.raw.length > 0) {
    return query.in(column, selection.raw);
  }
  return query;
}

/**
 * The whole query, built once and used by both the table and the export, so the file an
 * admin downloads can never disagree with the rows they were looking at.
 *
 * Built from scratch each time rather than mutated from a shared base: PostgREST builders
 * accumulate state, and a base object reused across two calls would carry the first call's
 * range into the second.
 */
function buildHistoryQuery(filters: AttendanceHistoryFilters, withCount: boolean) {
  let query = withCount
    ? supabase.from('attendance_records').select(SELECT, { count: 'exact' })
    : supabase.from('attendance_records').select(SELECT);

  query = query.gte('session_date', filters.from).lte('session_date', filters.to);

  // Each band is one predicate on the program's type plus one on the discriminator.
  //
  // Pinning `virtual_class_key = ''` on the three program-typed bands is not optional: a
  // Chinese row carries its child's *after-school* program, so without it every Chinese and
  // Math record would also be counted under After School — 579 rows where the honest answer
  // is 379.
  if (filters.band === 'afterschool') {
    query = query.eq('program.type', 'after-school').eq('virtual_class_key', REAL_SESSION_KEY);
  } else if (filters.band === 'camp') {
    query = query.eq('program.type', 'camp').eq('virtual_class_key', REAL_SESSION_KEY);
  } else if (filters.band === 'enrichment') {
    // Everything-else, keeping the fallback shape `pillarOfProgramType` uses: a program type
    // nobody has thought of yet lands here rather than vanishing from every band.
    query = query
      .not('program.type', 'in', `(${NAMED_PROGRAM_TYPES.join(',')})`)
      .eq('virtual_class_key', REAL_SESSION_KEY);
  } else if (filters.band === 'chinese' || filters.band === 'math') {
    // One class if a grade was chosen, otherwise the whole subject.
    query = filters.virtualClassKey
      ? query.eq('virtual_class_key', filters.virtualClassKey)
      : query.like('virtual_class_key', `${filters.band}:%`);
  }

  // Grade and school live as free text in `students.metadata`, so the filter matches the raw
  // values behind a label rather than the label — `'TK'` and `'Incoming TK'` are one grade.
  // Resolved from the students on file (see `buildAttendanceFacets`), which is the only
  // reliable way back from what a person picked to what the column holds.
  query = applyFacet(query, 'student.metadata->>grade', filters.grade);
  query = applyFacet(query, 'student.metadata->>school', filters.school);

  if (filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters.unauthorizedOnly) {
    query = query.eq('pickup_is_authorized', false);
  }

  const student = escapeSearchTerm(filters.studentSearch);
  if (student) {
    // Scoped to the embedded students table, which is joined with `!inner` — so a row whose
    // student does not match drops out of the result rather than coming back with a null
    // student attached.
    query = query.or(`first_name.ilike.%${student}%,last_name.ilike.%${student}%`, {
      referencedTable: 'student',
    });
  }

  const collector = escapeSearchTerm(filters.collectorSearch);
  if (collector) {
    query = query.or(
      `picked_up_by_name.ilike.%${collector}%,checked_in_by_name.ilike.%${collector}%`,
    );
  }

  return query.order('session_date', { ascending: false }).order('student_id', { ascending: true });
}

export const attendanceHistoryKeys = {
  page: (filters: AttendanceHistoryFilters, page: number) =>
    ['attendance-history', filters, page] as const,
};

/**
 * One page of history, newest day first, then by student — so a printed page reads like a
 * roster rather than like the order rows happened to be written in.
 */
export function useAttendanceHistory(filters: AttendanceHistoryFilters, page: number) {
  return useQuery<AttendanceHistoryPage>({
    queryKey: attendanceHistoryKeys.page(filters, page),
    queryFn: async () => {
      const start = page * HISTORY_PAGE_SIZE;
      const { data, error, count } = await buildHistoryQuery(filters, true).range(
        start,
        start + HISTORY_PAGE_SIZE - 1,
      );
      if (error) throw error;

      return {
        rows: ((data ?? []) as unknown as HistoryRowShape[]).map(toHistoryRow),
        total: count ?? 0,
      };
    },
    // History is about days already past; it does not need to chase live edits.
    staleTime: 60_000,
    // Keeps the previous page on screen while the next one loads, so paging through does
    // not blank the table on every click.
    placeholderData: previous => previous,
  });
}

/**
 * Every row matching the filters, for the export.
 *
 * Paged rather than one big request because PostgREST caps a response at a thousand rows and
 * would otherwise hand back a silently truncated file — the worst possible outcome for an
 * artefact someone will treat as a complete record.
 */
export async function fetchAllAttendanceHistory(
  filters: AttendanceHistoryFilters,
): Promise<{ rows: AttendanceHistoryRow[]; truncated: boolean }> {
  const rows: AttendanceHistoryRow[] = [];

  for (let start = 0; start < EXPORT_ROW_CAP; start += EXPORT_CHUNK) {
    const { data, error } = await buildHistoryQuery(filters, false).range(
      start,
      start + EXPORT_CHUNK - 1,
    );
    if (error) throw error;

    const chunk = (data ?? []) as unknown as HistoryRowShape[];
    rows.push(...chunk.map(toHistoryRow));
    // A short chunk means the server ran out of rows — the only reliable signal that the
    // range is exhausted, since a full chunk may or may not have more behind it.
    if (chunk.length < EXPORT_CHUNK) return { rows, truncated: false };
  }

  return { rows, truncated: true };
}


export const attendanceFacetKeys = { all: ['attendance-facets'] as const };

/**
 * Grade and school options, built from the students on file.
 *
 * Every student, not only the active ones: this feeds an archive, and a child who left in
 * June is exactly who someone is looking for in September. The whole table is a few hundred
 * rows of two fields, so it costs one small query held for five minutes.
 */
export function useAttendanceFacets() {
  return useQuery<AttendanceFacets>({
    queryKey: attendanceFacetKeys.all,
    queryFn: async () => {
      const { data, error } = await supabase.from('students').select('metadata');
      if (error) throw error;
      return buildAttendanceFacets((data ?? []) as FacetStudent[]);
    },
    staleTime: 5 * 60_000,
  });
}
